"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.apiContract = exports.API_CONTRACT_RESERVED_ENDPOINTS = void 0;
const core_1 = require("@ts-rest/core");
const zod_1 = require("zod");
const enums_1 = require("../../enums");
const project_1 = require("../../model/project/project");
const events_1 = require("../../model/project/events");
const attachments_1 = require("../../model/attachments");
const chat_1 = require("../../model/project/chat");
const agent_1 = require("../../model/agent/agent");
const types_1 = require("../../model/chats/types");
const project_template_1 = require("../../model/project/project-template");
const tone_1 = require("../../model/user/tone");
const persona_1 = require("../../model/user/persona");
const project_update_1 = require("../../model/project/project-update");
const c = (0, core_1.initContract)();
exports.API_CONTRACT_RESERVED_ENDPOINTS = {
    sendMessageToChat: { method: "POST", path: "/api/chats/:chatId" },
};
exports.apiContract = c.router({
    getAccount: {
        method: "GET",
        path: "/account",
        responses: {
            200: zod_1.z.strictObject({
                uid: zod_1.z.string(),
                access: zod_1.z.strictObject({
                    createOrg: zod_1.z.boolean(),
                }),
                orgs: zod_1.z.array(zod_1.z.strictObject({
                    id: zod_1.z.string(),
                    userId: zod_1.z.string(),
                    name: zod_1.z.string(),
                })),
            }),
        },
        summary: "Get the current user's account",
    },
    createOrg: {
        method: "POST",
        path: "/org/",
        responses: {
            201: zod_1.z.strictObject({
                id: zod_1.z.string(),
                name: zod_1.z.string(),
            }),
        },
        body: zod_1.z.strictObject({
            name: zod_1.z.string().min(2),
        }),
    },
    createOrgUser: {
        method: "POST",
        path: "/org/user",
        responses: {
            201: zod_1.z.strictObject({
                name: zod_1.z.string(),
                id: zod_1.z.string(),
            }),
        },
        body: zod_1.z.strictObject({
            name: zod_1.z.string().min(2).max(50),
            title: zod_1.z.string().max(50).optional(),
            department: zod_1.z.string().max(50).optional(),
            email: zod_1.z.string().email(),
        }),
        query: zod_1.z.strictObject({
            emailInvite: zod_1.z
                .enum(["true", "false"])
                .transform((value) => value === "true")
                .optional(),
        }),
        summary: "Create a new user in a specific organization",
    },
    updateOrgUser: {
        method: "PATCH",
        path: "/org/user/:userId",
        responses: {
            200: zod_1.z.strictObject({
                id: zod_1.z.string(),
                name: zod_1.z.string(),
                title: zod_1.z.string().optional(),
                department: zod_1.z.string().optional(),
                assistantTonePreferences: tone_1.assistantTonePreferencesSchema.optional(),
                persona: persona_1.personaSchema.optional(),
            }),
        },
        body: zod_1.z.strictObject({
            name: zod_1.z.string().min(2).max(50).optional(),
            title: zod_1.z.string().min(2).max(50).optional(),
            department: zod_1.z.string().max(50).optional(),
            assistantTonePreferences: tone_1.assistantTonePreferencesSchema.optional(),
            persona: persona_1.personaSchema.optional(),
        }),
        summary: "Update name, title, department, persona, or assistant tone preferences of an organization user",
    },
    importSlackUsers: {
        method: "POST",
        path: "/org/slack/import-users",
        body: zod_1.z.any().optional(),
        responses: {
            200: zod_1.z.strictObject({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    getOrg: {
        method: "GET",
        path: "/org",
        responses: {
            200: zod_1.z.strictObject({
                id: zod_1.z.string(),
                name: zod_1.z.string(),
                logo: zod_1.z
                    .object({
                    urlBlack: zod_1.z.string(),
                    urlWhite: zod_1.z.string(),
                })
                    .optional(),
                meta: zod_1.z
                    .object({
                    techOrgSize: zod_1.z.number().optional(),
                    companySize: zod_1.z.number().optional(),
                    stage: zod_1.z.nativeEnum(enums_1.CompanyStage).optional(),
                })
                    .optional(),
                users: zod_1.z.array(zod_1.z.strictObject({
                    name: zod_1.z.string(),
                    title: zod_1.z.string().optional(),
                    department: zod_1.z.string().optional(),
                    email: zod_1.z.string(),
                    id: zod_1.z.string(),
                    hasAccount: zod_1.z.boolean(),
                    linkedAccounts: zod_1.z.array(zod_1.z.string()),
                    imageUrl: zod_1.z.string().optional(),
                    assistantTonePreferences: tone_1.assistantTonePreferencesSchema.optional(),
                    persona: persona_1.personaSchema.optional(),
                })),
                officeSuiteProvider: zod_1.z.enum(["google", "microsoft"]),
            }),
        },
        summary: "Get current organization",
    },
    updateOrgMeta: {
        method: "PATCH",
        path: "/org/meta",
        body: zod_1.z.object({
            techOrgSize: zod_1.z.number().optional(),
            companySize: zod_1.z.number().optional(),
            stage: zod_1.z.nativeEnum(enums_1.CompanyStage).optional(),
        }),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
    },
    createUserInvite: {
        method: "PUT",
        path: "/org/user/:userId/invite",
        responses: {
            201: zod_1.z.strictObject({
                token: zod_1.z.string(),
                validUntil: zod_1.z.date(),
            }),
        },
        query: zod_1.z.strictObject({
            sendEmail: zod_1.z.enum(["true", "false"]).transform((value) => value === "true"),
        }),
        body: zod_1.z.any(),
        summary: "Create a invite for a user to join the org with their account",
    },
    acceptInvite: {
        method: "GET",
        path: "/invite/:token/accept",
        responses: {
            200: zod_1.z.strictObject({
                orgId: zod_1.z.string(),
            }),
        },
        summary: "Accept an invite to join an organization with the users account",
    },
    getInvite: {
        method: "GET",
        path: "/invite/:token",
        responses: {
            200: zod_1.z.strictObject({
                orgName: zod_1.z.string(),
                validUntil: zod_1.z.string(),
            }),
        },
        summary: "Get context about an invite",
    },
    getProfilePicture: {
        method: "GET",
        path: "/org/user/:userId/picture",
        responses: {
            200: zod_1.z.string().describe("base64 string representing the image"),
            204: zod_1.z.string(),
        },
    },
    linkOauthAccount: {
        method: "GET",
        path: "/account/profile/:service/authorize",
        responses: {
            200: zod_1.z.strictObject({
                authUrl: zod_1.z.string(),
            }),
        },
    },
    createMeetingTrackerEmail: {
        method: "POST",
        path: "/meeting-tracker",
        body: zod_1.z.object({
            projectId: zod_1.z.string(),
            scope: zod_1.z.array(zod_1.z.string()),
        }),
        responses: {
            200: zod_1.z.object({
                emailAddress: zod_1.z.string(),
            }),
        },
    },
    uploadMeetingTranscript: {
        method: "POST",
        path: "/tracked-meeting/:meetingId/transcript",
        body: zod_1.z.object({
            transcript: zod_1.z.string(),
        }),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
    },
    getOrgReadiness: {
        method: "GET",
        path: "/projects-org-readiness",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.object({
                    usersReady: zod_1.z.boolean(),
                    backgroundCheckReady: zod_1.z.boolean(),
                }),
            }),
        },
    },
    createCreateProjectChat: {
        method: "POST",
        path: "/chat/create-project",
        query: zod_1.z.object({
            reviseProjectId: zod_1.z.string().optional(),
        }),
        body: zod_1.z.any(),
        responses: {
            200: zod_1.z.object({
                chatId: zod_1.z.string(),
            }),
        },
    },
    createProject: {
        method: "POST",
        path: "/projects",
        body: zod_1.z.object({
            createProjectChatId: zod_1.z.string(),
        }),
        responses: {
            200: zod_1.z.object({
                id: zod_1.z.string(),
            }),
        },
    },
    reviseProject: {
        method: "POST",
        path: "/projects/:id/revise",
        body: zod_1.z.object({
            createProjectChatId: zod_1.z.string(),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    getProject: {
        method: "GET",
        path: "/projects/:id",
        responses: {
            200: zod_1.z.object({
                data: project_1.httpProjectSchema,
            }),
        },
    },
    getProjectTasks: {
        method: "GET",
        path: "/projects/:id/tasks",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(project_1.projectTaskSchema),
            }),
        },
    },
    updateProject: {
        method: "PATCH",
        path: "/projects/:id",
        body: project_1.httpProjectSchema.pick({ name: true, collaborators: true, creatorId: true }).partial(),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Update project name, owner, or make project public (collaborators: null)",
    },
    listProjects: {
        method: "GET",
        path: "/projects",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(zod_1.z.object({
                    id: zod_1.z.string(),
                    name: zod_1.z.string(),
                    creatorId: zod_1.z.string(),
                    createdAt: zod_1.z.string(),
                    progress: zod_1.z
                        .object({
                        deliverableCompletion: zod_1.z
                            .number()
                            .describe("share of deliverables completed in project"),
                        taskCompletion: zod_1.z.number().describe("share of tasks completed in project"),
                        statusCounts: zod_1.z.object({
                            done: zod_1.z.number(),
                            started: zod_1.z.number(),
                            on_hold: zod_1.z.number(),
                            todo: zod_1.z.number(),
                            total: zod_1.z.number(),
                        }),
                        workstreams: zod_1.z.array(zod_1.z.object({
                            workstreamId: zod_1.z.string(),
                            workstreamName: zod_1.z.string(),
                            deliverableCompletion: zod_1.z
                                .number()
                                .describe("share of deliverables completed in this workstream"),
                            statusCounts: zod_1.z.object({
                                done: zod_1.z.number(),
                                started: zod_1.z.number(),
                                on_hold: zod_1.z.number(),
                                todo: zod_1.z.number(),
                                total: zod_1.z.number(),
                            }),
                            deliverables: zod_1.z.array(zod_1.z.object({
                                deliverableId: zod_1.z.string(),
                                taskCompletion: zod_1.z
                                    .number()
                                    .describe("share of tasks completed in this deliverable"),
                                progress: zod_1.z.object({
                                    taskCount: zod_1.z.number(),
                                    tasksCompleted: zod_1.z.number(),
                                }),
                            })),
                        })),
                    })
                        .nullable(),
                    status: zod_1.z.enum(["draft", "not_started", "on_track", "delayed", "completed"]).nullable(),
                    timeline: zod_1.z.object({
                        from: zod_1.z.string().date().nullable(),
                        to: zod_1.z.string().date().nullable(),
                    }),
                })),
            }),
        },
    },
    deleteProject: {
        method: "DELETE",
        path: "/projects/:id",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    addProjectCollaborator: {
        method: "POST",
        path: "/projects/:projectId/collaborators",
        body: zod_1.z.object({
            userIds: zod_1.z.array(zod_1.z.string()).min(1),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Add collaborators to a project (creator only)",
    },
    removeProjectCollaborator: {
        method: "DELETE",
        path: "/projects/:projectId/collaborators/:userId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Remove a collaborator from a project (creator only)",
    },
    getProjectEvents: {
        method: "GET",
        path: "/projects/:id/events",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(events_1.projectEventSchema),
            }),
        },
    },
    kickOffProject: {
        method: "POST",
        path: "/projects/:id/kick-off",
        body: zod_1.z.object({
            alignmentWorkstream: project_1.projectWorkstreamSchema.pick({ name: true, description: true }).optional(),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    generateDraftProjectTimeline: {
        method: "GET",
        path: "/projects/:projectId/timeline",
        responses: {
            200: zod_1.z.object({
                deliverables: zod_1.z.array(zod_1.z.object({
                    workstreamId: zod_1.z.string(),
                    deliverableId: zod_1.z.string(),
                    displayName: zod_1.z.string(),
                    durationDays: zod_1.z.number(),
                    offsetDays: zod_1.z.number(),
                    dependencies: zod_1.z.array(zod_1.z.object({ workstreamId: zod_1.z.string(), deliverableId: zod_1.z.string() })),
                })),
            }),
        },
    },
    updateDeliverableTimelines: {
        method: "POST",
        path: "/projects/:projectId/deliverables/timelines",
        body: zod_1.z.object({
            updates: zod_1.z.array(zod_1.z.object({
                workstreamId: zod_1.z.string(),
                deliverableId: zod_1.z.string(),
                startDate: zod_1.z.string().date(),
                endDate: zod_1.z.string().date(),
            })),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    updateWorkstream: {
        method: "PATCH",
        path: "/projects/:projectId/workstreams/:workstreamId",
        body: project_1.projectWorkstreamSchema.pick({ name: true, description: true, ownerId: true }).partial(),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
    },
    updateDeliverable: {
        method: "PATCH",
        path: "/projects/:projectId/workstreams/:workstreamId/deliverables/:deliverableId",
        body: project_1.workstreamDeliverableSchema.pick({ name: true, description: true, ownerId: true }).partial(),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
    },
    deleteWorkstream: {
        method: "DELETE",
        path: "/projects/:projectId/workstreams/:workstreamId",
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
        summary: "Delete a workstream and its orphaned tasks",
    },
    deleteDeliverable: {
        method: "DELETE",
        path: "/projects/:projectId/workstreams/:workstreamId/deliverables/:deliverableId",
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
        summary: "Delete a deliverable and its orphaned tasks",
    },
    reorderWorkstreams: {
        method: "PATCH",
        path: "/projects/:projectId/outline/reorder",
        body: zod_1.z.object({
            workstreamIds: zod_1.z.array(zod_1.z.string()),
        }),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
        summary: "Reorder workstreams by providing the new order of IDs",
    },
    reorderDeliverables: {
        method: "PATCH",
        path: "/projects/:projectId/workstreams/:workstreamId/deliverables/reorder",
        body: zod_1.z.object({
            deliverableIds: zod_1.z.array(zod_1.z.string()),
        }),
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
        summary: "Reorder deliverables within a workstream",
    },
    updateProjectTasks: {
        method: "PATCH",
        path: "/projects/:projectId/tasks",
        body: zod_1.z.object({
            removeTasks: zod_1.z.array(zod_1.z.string()).optional().describe("ids of tasks to remove"),
            createTasks: zod_1.z
                .array(project_1.projectTaskSchema.pick({
                deliverableId: true,
                workstreamId: true,
                title: true,
                description: true,
                execution: true,
            }))
                .optional(),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    executeTask: {
        method: "POST",
        path: "/projects/:projectId/tasks/:taskId/execute",
        body: zod_1.z.object({}),
        responses: {
            200: zod_1.z.discriminatedUnion("type", [
                zod_1.z.object({
                    type: zod_1.z.literal("schedule_meeting"),
                    data: zod_1.z.object({
                        title: zod_1.z.string(),
                        description: zod_1.z.string(),
                        duration: zod_1.z.number(),
                        suggestedParticipantsUserIds: zod_1.z.array(zod_1.z.string()),
                    }),
                }),
                zod_1.z.object({
                    type: zod_1.z.literal("draft_document"),
                    data: zod_1.z.object({
                        fileId: zod_1.z.string(),
                        url: zod_1.z.string(),
                    }),
                }),
                zod_1.z.object({
                    type: zod_1.z.literal("do_research"),
                    data: zod_1.z.object({
                        searchQuery: zod_1.z.string(),
                    }),
                }),
            ]),
        },
    },
    getScheduledProjectMeetings: {
        method: "GET",
        path: "/projects/:id/scheduled-meetings",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(zod_1.z.object({
                    id: zod_1.z.string(),
                    title: zod_1.z.string(),
                    scope: zod_1.z.array(zod_1.z.string()),
                    description: zod_1.z.string().optional(),
                    start: zod_1.z.string(),
                    end: zod_1.z.string(),
                    inviteeCount: zod_1.z.number(),
                    transcript: zod_1.z.string().optional(),
                    summary: zod_1.z.string().optional(),
                    hasBotDeployed: zod_1.z.boolean().optional(),
                })),
            }),
        },
    },
    transcribeAudio: {
        method: "POST",
        path: "/chat/transcribe-audio",
        body: zod_1.z.object({
            audioBase64: zod_1.z.string(),
        }),
        responses: {
            200: zod_1.z.object({
                transcription: zod_1.z.string(),
            }),
        },
    },
    updateProjectTask: {
        method: "PATCH",
        path: "/projects/:projectId/tasks/:taskId",
        body: project_1.projectTaskSchema.pick({ status: true, assignee: true }).partial(),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    deleteProjectTask: {
        method: "DELETE",
        path: "/projects/:projectId/tasks/:taskId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    getProjectAttachments: {
        method: "GET",
        path: "/projects/:projectId/attachments",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(attachments_1.httpAttachmentSchema),
            }),
        },
    },
    getProjectSummaries: {
        method: "GET",
        path: "/projects/:projectId/summaries",
        responses: {
            200: zod_1.z.object({
                description: zod_1.z.string(),
                latestDevelopments: zod_1.z.string().nullable(),
            }),
        },
    },
    createProjectChat: {
        method: "POST",
        path: "/projects/:projectId/chats",
        body: zod_1.z.object({
            uiContext: chat_1.projectChatUiContextSchema,
        }),
        responses: {
            200: zod_1.z.object({
                chatId: zod_1.z.string(),
            }),
        },
    },
    getChat: {
        method: "GET",
        path: "/chats/:chatId",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.object({
                    title: zod_1.z.string(),
                    createdAt: zod_1.z.string(),
                    type: zod_1.z.nativeEnum(types_1.ChatType),
                    context: zod_1.z.any(),
                    messages: zod_1.z.array(zod_1.z.any().describe("CoreMessage messages (AI SDK)")),
                }),
            }),
        },
    },
    listChats: {
        method: "GET",
        path: "/chats",
        query: zod_1.z.object({
            type: zod_1.z.string().optional(),
            scope: zod_1.z
                .string()
                .optional()
                .describe("comma separated list of scope filter, e.g. `project,68b6e91b01b87a5d6e1d55da`"),
        }),
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(zod_1.z.object({
                    id: zod_1.z.string(),
                    title: zod_1.z.string(),
                    createdAt: zod_1.z.string(),
                    context: zod_1.z.any(),
                })),
            }),
        },
    },
    deleteChat: {
        method: "DELETE",
        path: "/chats/:chatId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    uploadFile: {
        method: "POST",
        path: "/files",
        contentType: "multipart/form-data",
        body: zod_1.z.any(),
        responses: {
            200: zod_1.z.object({
                id: zod_1.z.string(),
                uploadedAt: zod_1.z.string(),
                uploadedBy: zod_1.z.string(),
                mimeType: zod_1.z.string(),
            }),
        },
    },
    deleteFile: {
        method: "DELETE",
        path: "/files/:fileId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    createProjectAttachment: {
        method: "POST",
        path: "/projects/:projectId/attachments",
        body: zod_1.z.object({
            target: zod_1.z.array(zod_1.z.string()).optional(),
            attachments: zod_1.z.array(zod_1.z.discriminatedUnion("type", [
                zod_1.z.strictObject({
                    type: zod_1.z.literal(enums_1.AttachmentReferenceType.URL),
                    name: zod_1.z.string(),
                    url: zod_1.z.string(),
                }),
                zod_1.z.strictObject({
                    type: zod_1.z.literal(enums_1.AttachmentReferenceType.File),
                    fileId: zod_1.z.string(),
                }),
            ])),
        }),
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    removeProjectAttachment: {
        method: "DELETE",
        path: "/projects/:projectId/attachments/:attachmentId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
    },
    applyDocumentUpdate: {
        method: "POST",
        path: "/projects/:projectId/documents/apply-update",
        body: zod_1.z.strictObject({
            proposalId: zod_1.z.string(),
            editIds: zod_1.z.array(zod_1.z.string()),
        }),
        responses: {
            200: zod_1.z.strictObject({
                appliedCount: zod_1.z.number(),
                skippedCount: zod_1.z.number(),
                documentUrl: zod_1.z.string(),
            }),
            404: zod_1.z.strictObject({
                error: zod_1.z.string(),
            }),
        },
        summary: "Apply proposed document edits",
    },
    getFileContents: {
        method: "GET",
        path: "/files/:fileId/contents",
        responses: {
            200: zod_1.z.object({
                mimeType: zod_1.z.string(),
                base64Data: zod_1.z.string(),
            }),
        },
    },
    getProjectNextStepsRecommendation: {
        method: "GET",
        path: "/projects/:projectId/next-steps-recommendation",
        responses: {
            200: zod_1.z.object({
                text: zod_1.z.string(),
            }),
        },
    },
    createAgent: {
        method: "POST",
        path: "/agents",
        body: agent_1.createAgentInputSchema,
        responses: {
            201: zod_1.z.object({
                id: zod_1.z.string(),
                createdAt: zod_1.z.string(),
            }),
        },
        summary: "Create a new custom agent",
    },
    updateAgent: {
        method: "PATCH",
        path: "/agents/:agentId",
        body: agent_1.updateAgentInputSchema,
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Update an existing agent",
    },
    deleteAgent: {
        method: "DELETE",
        path: "/agents/:agentId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Delete an agent",
    },
    getAgent: {
        method: "GET",
        path: "/agents/:agentId",
        responses: {
            200: zod_1.z.object({
                data: agent_1.agentSchema,
            }),
        },
        summary: "Get a specific agent by ID",
    },
    listAgents: {
        method: "GET",
        path: "/agents",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(agent_1.agentMetaSchema),
            }),
        },
        summary: "List all agents in the organization",
    },
    createAgentChat: {
        method: "POST",
        path: "/agents/:agentId/chats",
        query: zod_1.z.object({
            projectId: zod_1.z.string(),
        }),
        body: zod_1.z.object({
            uiContext: chat_1.projectChatUiContextSchema,
        }),
        responses: {
            200: zod_1.z.object({
                chatId: zod_1.z.string(),
            }),
        },
        summary: "Create a new chat with an agent",
    },
    createProjectUpdate: {
        method: "POST",
        path: "/projects/:projectId/updates",
        body: project_update_1.createSentUpdateInputSchema,
        responses: {
            201: zod_1.z.object({
                id: zod_1.z.string(),
                sentAt: zod_1.z.string(),
            }),
        },
        summary: "Save a sent project status update",
    },
    deleteProjectUpdate: {
        method: "DELETE",
        path: "/projects/:projectId/updates/:updateId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Delete a project update",
    },
    saveProjectAsTemplate: {
        method: "POST",
        path: "/templates",
        body: project_template_1.createProjectTemplateInputSchema,
        responses: {
            201: zod_1.z.object({
                id: zod_1.z.string(),
                createdAt: zod_1.z.string(),
            }),
        },
        summary: "Save a project as a template",
    },
    listProjectTemplates: {
        method: "GET",
        path: "/templates",
        responses: {
            200: zod_1.z.object({
                data: zod_1.z.array(project_template_1.projectTemplateListSchema),
            }),
        },
        summary: "List all available project templates",
    },
    getProjectTemplate: {
        method: "GET",
        path: "/templates/:templateId",
        responses: {
            200: zod_1.z.object({
                data: project_template_1.projectTemplateSchema,
            }),
        },
        summary: "Get a specific template by ID",
    },
    createProjectFromTemplate: {
        method: "POST",
        path: "/templates/:templateId/create-project",
        body: zod_1.z.object({
            projectName: zod_1.z.string().min(1, "Project name is required"),
        }),
        responses: {
            201: zod_1.z.object({
                projectId: zod_1.z.string(),
            }),
        },
        summary: "Create a new project from a template",
    },
    deleteProjectTemplate: {
        method: "DELETE",
        path: "/templates/:templateId",
        responses: {
            200: zod_1.z.object({
                ok: zod_1.z.boolean(),
            }),
        },
        summary: "Delete a project template",
    },
    createGoogleDriveConnectSession: {
        method: "POST",
        path: "/google-drive/connect-session",
        body: zod_1.z.object({
            allowedIntegrations: zod_1.z.array(zod_1.z.string()).optional(),
        }),
        responses: {
            200: zod_1.z.object({
                connectLink: zod_1.z.string(),
                expiresAt: zod_1.z.string().optional(),
            }),
        },
        summary: "Create a Nango connect session for Google Drive OAuth via Contexto",
    },
    getGoogleDriveAccessToken: {
        method: "GET",
        path: "/google-drive/access-token",
        responses: {
            200: zod_1.z.object({
                accessToken: zod_1.z.string(),
                pickerApiKey: zod_1.z.string(),
            }),
        },
        summary: "Get a Google Drive OAuth access token for the Picker via Contexto",
    },
    importGoogleDriveFiles: {
        method: "POST",
        path: "/google-drive/file-contents",
        body: zod_1.z.object({
            files: zod_1.z.array(zod_1.z.object({
                fileId: zod_1.z.string(),
                mimeType: zod_1.z.string().optional(),
                name: zod_1.z.string().optional(),
            })),
        }),
        responses: {
            200: zod_1.z.object({
                files: zod_1.z.array(zod_1.z.object({
                    fileId: zod_1.z.string(),
                    name: zod_1.z.string().nullable(),
                    mimeType: zod_1.z.string().nullable(),
                    content: zod_1.z.string(),
                    error: zod_1.z.string().nullable(),
                })),
            }),
        },
        summary: "Fetch Google Drive file contents via Contexto",
    },
    setupGoogleDrive: {
        method: "POST",
        path: "/google-drive/setup",
        body: zod_1.z.object({
            storageLevel: zod_1.z.enum(["full", "summary", "reference"]).optional(),
            accessMode: zod_1.z.enum(["full", "restricted"]).optional(),
        }),
        responses: {
            200: zod_1.z.object({
                sourceConfigId: zod_1.z.string(),
                jobId: zod_1.z.string(),
                pickerUrl: zod_1.z.string().optional(),
            }),
        },
        summary: "Create source config and trigger sync after Google Drive OAuth",
    },
    getGoogleDriveStatus: {
        method: "GET",
        path: "/google-drive/status",
        responses: {
            200: zod_1.z.object({
                connected: zod_1.z.boolean(),
                sourceConfigId: zod_1.z.string().nullable(),
                storageLevel: zod_1.z.enum(["full", "summary", "reference"]).nullable(),
                accessMode: zod_1.z.enum(["full", "restricted"]).nullable(),
                docsIndexed: zod_1.z.number().nullable(),
            }),
        },
        summary: "Check if user has a connected Google Drive source config",
    },
    deleteGoogleDrive: {
        method: "DELETE",
        path: "/google-drive",
        responses: {
            200: zod_1.z.object({ ok: zod_1.z.boolean() }),
        },
        summary: "Delete Google Drive source config and all indexed data",
    },
    getGoogleDriveSyncStatus: {
        method: "GET",
        path: "/google-drive/sync-status/:jobId",
        responses: {
            200: zod_1.z.object({
                status: zod_1.z.string(),
                docsFound: zod_1.z.number(),
                docsIndexed: zod_1.z.number(),
                docsFailed: zod_1.z.number(),
                progress: zod_1.z.number().nullable(),
            }),
        },
        summary: "Get sync job progress for Google Drive indexing",
    },
    createGoogleDriveCompare: {
        method: "POST",
        path: "/chats/:chatId/google-drive-compare",
        body: zod_1.z.object({
            sourceToolCallId: zod_1.z.string(),
            curatedExternalIds: zod_1.z.array(zod_1.z.string()).min(1),
        }),
        responses: {
            200: zod_1.z.object({
                experimentId: zod_1.z.string(),
                optionA: zod_1.z.object({
                    text: zod_1.z.string(),
                }),
                optionB: zod_1.z.object({
                    text: zod_1.z.string(),
                }),
            }),
        },
        summary: "Generate two blind Google Drive-based draft options",
    },
    selectGoogleDriveCompare: {
        method: "POST",
        path: "/chats/:chatId/google-drive-compare/:experimentId/select",
        body: zod_1.z.object({
            selectedOption: zod_1.z.enum(["A", "B"]),
            reason: zod_1.z.string().trim().max(2000).optional(),
        }),
        responses: {
            200: zod_1.z.object({
                winningText: zod_1.z.string(),
            }),
        },
        summary: "Select the winning blind draft and commit it to chat",
    },
}, {
    pathPrefix: "/api",
});
